import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { Input } from '@/components/ui/input'
import type { Contact } from '@/services/contactsApi'
import type { ChannelInstanceResponse } from '@/services/channelsApi'
import { type OrderResponse } from '@/services/ordersApi'
import { buildOrderSourceLabel, getOrderStatusBadge } from './ordersUx'
import { CheckCircle2, Clock3, Home, Loader2, Package, Plus, Search, ShoppingCart, Truck, Trash2, User } from 'lucide-react'
import type { Product } from '@/lib/api/products'
import type { Dispatch, SetStateAction } from 'react'

type WizardStep = 1 | 2 | 3

export type ContactSummary = {
  id: string
  name: string | null
  phone: string | null
  waContact: string | null
  email: string | null
}
type ChannelInstanceSummary = Pick<ChannelInstanceResponse, 'id' | 'instanceName' | 'channelType'>

function getFulfillmentModeLabel(method: string) {
  if (method === 'delivery') return 'Delivery'
  if (method === 'pickup') return 'Pickup'
  if (method === 'cash_on_delivery') return 'Envío cobro destino'
  if (method === 'shipping') return 'Envío nacional'
  if (method === 'store_pickup') return 'Retiro en tienda'
  return method || 'Sin definir'
}

type OrderLine = { productId: string; quantity: number }

export type EditOrderForm = {
  source: 'manual' | 'catalog' | 'checkout'
  contactId: string
  conversationId: string
  channelInstanceId: string
  customerName: string
  customerPhone: string
  customerEmail: string
  fulfillmentMethod: string
  fulfillmentAddress: string
  fulfillmentReference: string
  notes: string
  items: OrderLine[]
}

type EditOrderWizardModalProps = {
  isOpen: boolean
  onClose: () => void
  order: OrderResponse | null
  wizardStep: WizardStep
  setWizardStep: Dispatch<SetStateAction<WizardStep>>
  goToNextStep: () => void
  goToPreviousStep: () => void
  form: EditOrderForm
  setForm: Dispatch<SetStateAction<EditOrderForm>>
  selectedContact: ContactSummary | null
  onSelectContact: (contact: Contact) => void
  contactSearch: string
  setContactSearch: (value: string) => void
  contacts: Contact[]
  productSearch: string
  setProductSearch: (value: string) => void
  filteredProducts: Product[]
  availableProducts: Product[]
  addProductToOrder: (product: Product) => void
  addEmptyItem: () => void
  updateItem: (index: number, patch: Partial<OrderLine>) => void
  removeItem: (index: number) => void
  appendNoteTemplate: (template: string) => void
  selectedProducts: Array<Product & { quantity: number; lineTotalCents: number }>
  totals: { subtotal: number; total: number }
  pickupOptions: string[]
  editChannelInstance: ChannelInstanceSummary | null
  recentOrders: OrderResponse[]
  isDeliveryMode: boolean
  isPickupMode: boolean
  fulfillmentMode: string
  stepOneReady: boolean
  stepTwoReady: boolean
  stepThreeReady: boolean
  isSaving: boolean
  onSubmit: () => void
  money: (value: number, currency?: string) => string
  when: (value: string) => string
}

const WIZARD_STEPS = [
  { step: 1 as const, title: 'Cliente', subtitle: 'Busca o crea un cliente' },
  { step: 2 as const, title: 'Productos', subtitle: 'Arma la orden' },
  { step: 3 as const, title: 'Detalles', subtitle: 'Entrega y confirmación' },
]

const NOTE_TEMPLATES = [
  'Entregar en horario comercial.',
  'Confirmar stock antes del despacho.',
  'Cliente solicita aviso previo.',
  'Retiro en tienda con nombre del titular.',
]

function EditOrderWizardModal({
  isOpen,
  onClose,
  order,
  wizardStep,
  setWizardStep,
  goToNextStep,
  goToPreviousStep,
  form,
  setForm,
  selectedContact,
  onSelectContact,
  contactSearch,
  setContactSearch,
  contacts,
  productSearch,
  setProductSearch,
  filteredProducts,
  availableProducts,
  addProductToOrder,
  addEmptyItem,
  updateItem,
  removeItem,
  appendNoteTemplate,
  selectedProducts,
  totals,
  pickupOptions,
  editChannelInstance,
  recentOrders,
  isDeliveryMode,
  isPickupMode,
  fulfillmentMode,
  stepOneReady,
  stepTwoReady,
  stepThreeReady,
  isSaving,
  onSubmit,
  money,
  when,
}: EditOrderWizardModalProps) {
  if (!order) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar orden" size="xl">
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-3">
          {WIZARD_STEPS.map((step) => {
            const isActive = wizardStep === step.step
            const isDone = wizardStep > step.step
            return (
              <button
                key={step.step}
                type="button"
                onClick={() => setWizardStep(step.step)}
                className={[
                  'rounded-2xl border px-4 py-3 text-left transition-all',
                  isActive ? 'border-[#075f55]/30 bg-[#075f55]/5 shadow-sm' : isDone ? 'border-emerald-200 bg-emerald-50/70' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                ].join(' ')}
              >
                <div className="flex items-center gap-2">
                  <span className={['flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold', isActive ? 'bg-[#075f55] text-white' : isDone ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'].join(' ')}>
                    {step.step}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                    <p className="text-xs text-slate-500">{step.subtitle}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {wizardStep === 1 && (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <User className="h-4 w-4 text-[#075f55]" />
              <h3 className="text-base font-semibold">Cliente y contexto</h3>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.35fr_0.95fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Buscar cliente existente</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Nombre, teléfono, email o WhatsApp" />
                  </div>
                </div>

                <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
                  {contacts.length > 0 ? contacts.map((contact) => {
                    const isSelected = form.contactId === contact.id
                    return (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => onSelectContact(contact)}
                        className={['w-full rounded-xl border px-3 py-3 text-left transition-colors', isSelected ? 'border-[#075f55]/30 bg-[#075f55]/5' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'].join(' ')}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{contact.name || contact.waContact}</p>
                            <p className="truncate text-xs text-slate-500">{contact.phone || contact.waContact}{contact.email ? ` · ${contact.email}` : ''}</p>
                          </div>
                          {isSelected && <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700">Seleccionado</span>}
                        </div>
                      </button>
                    )
                  }) : <p className="text-sm text-slate-500">No hay coincidencias.</p>}
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><User className="h-4 w-4" />Cliente seleccionado</div>
                  {selectedContact ? (
                    <div className="mt-3 space-y-1">
                      <p className="font-semibold text-slate-900">{selectedContact.name || 'Sin nombre'}</p>
                      <p className="text-sm text-slate-600">{selectedContact.phone || selectedContact.waContact || 'Sin teléfono'}</p>
                      {selectedContact.email ? <p className="text-sm text-slate-600">{selectedContact.email}</p> : null}
                    </div>
                  ) : <p className="mt-3 text-sm text-slate-500">Mantén el cliente actual o selecciona otro.</p>}
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Clock3 className="h-4 w-4" />Órdenes recientes</div>
                  <div className="mt-3 space-y-2">
                    {recentOrders.length > 0 ? recentOrders.slice(0, 3).map((recent) => (
                      <div key={recent.id} className="rounded-lg border border-white bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">{recent.orderNumber}</p>
                          <span className="text-xs text-slate-500">{getOrderStatusBadge(recent.status)?.label}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{when(recent.createdAt)} · {money(recent.totalCents, recent.currency)}</p>
                      </div>
                    )) : <p className="text-sm text-slate-500">No hay órdenes recientes.</p>}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Package className="h-4 w-4" />Contexto de la orden</div>
                  <div className="mt-3 grid gap-2 text-sm text-slate-600">
                    <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Origen</p>
                      <p className="mt-1 font-semibold text-slate-900">{buildOrderSourceLabel(form.source)}</p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Canal</p>
                      <p className="mt-1 font-semibold text-slate-900">{editChannelInstance ? `${editChannelInstance.instanceName} · ${editChannelInstance.channelType}` : 'Sin canal seleccionado'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Input value={form.customerName} onChange={(e) => setForm((current) => ({ ...current, customerName: e.target.value }))} placeholder="Nombre cliente" />
              <Input value={form.customerPhone} onChange={(e) => setForm((current) => ({ ...current, customerPhone: e.target.value }))} placeholder="+5491112345678" />
            </div>
            <Input type="email" value={form.customerEmail} onChange={(e) => setForm((current) => ({ ...current, customerEmail: e.target.value }))} placeholder="cliente@correo.com" />

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="button" className="bg-[#075f55] text-white hover:bg-[#064d46]" onClick={goToNextStep} disabled={!stepOneReady}>Siguiente</Button>
            </div>
          </div>
        )}

        {wizardStep === 2 && (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <Package className="h-4 w-4 text-[#075f55]" />
              <h3 className="text-base font-semibold">Productos</h3>
            </div>
            <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Buscar producto</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Nombre, SKU o descripción" />
                  </div>
                </div>
                <div className="grid gap-3">
                  {filteredProducts.length > 0 ? filteredProducts.map((product) => (
                    <button key={product.id} type="button" onClick={() => addProductToOrder(product)} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:border-[#075f55]/20 hover:bg-[#075f55]/5">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                        {product.imageUrl ? <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">{product.name}</p>
                        <p className="truncate text-xs text-slate-500">SKU {product.sku} · Stock {product.stock} · {money(product.priceCents, product.currency)}</p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#075f55] shadow-sm">Agregar</span>
                    </button>
                  )) : <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No hay resultados.</div>}
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-slate-900">Productos agregados</p><p className="text-xs text-slate-500">Cada línea conserva precio y cantidad actualizada.</p></div><Button type="button" variant="outline" onClick={addEmptyItem}><Plus className="mr-2 h-4 w-4" />Agregar otro producto</Button></div>
                  <div className="mt-3 space-y-3">
                    {form.items.map((item, index) => {
                      const selectedCatalogProduct = availableProducts.find((product) => product.id === item.productId) ?? null
                      const selectedSnapshotItem = order.items.find((entry) => entry.productId === item.productId) ?? null
                      const selectedProduct =
                        selectedCatalogProduct ??
                        selectedProducts.find((product) => product.id === item.productId) ??
                        (selectedSnapshotItem
                          ? {
                              id: selectedSnapshotItem.productId ?? '',
                              name: selectedSnapshotItem.name,
                              sku: selectedSnapshotItem.sku,
                              priceCents: selectedSnapshotItem.unitPriceCents,
                              currency: selectedSnapshotItem.currency,
                              stock: null,
                            }
                          : null)
                      const productOptions = [
                        ...availableProducts,
                        ...order.items
                          .filter((entry) => entry.productId && !availableProducts.some((product) => product.id === entry.productId))
                          .map((entry) => ({
                            id: entry.productId ?? '',
                            name: entry.name,
                            sku: entry.sku,
                            priceCents: entry.unitPriceCents,
                            currency: entry.currency,
                            stock: 0,
                          })),
                      ]
                      return (
                        <div key={`${item.productId || 'empty'}-${index}`} className="rounded-2xl border border-white bg-white p-4 shadow-sm ring-1 ring-slate-100">
                          {selectedProduct ? (
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{selectedProduct.name}</p><p className="text-xs text-slate-500">SKU {selectedProduct.sku} · Stock {selectedProduct.stock ?? 'N/D'} · {money(selectedProduct.priceCents, selectedProduct.currency)}</p></div><Button type="button" variant="outline" onClick={() => removeItem(index)} disabled={form.items.length === 1} className="shrink-0"><Trash2 className="mr-2 h-4 w-4" />Eliminar</Button></div>
                              <div className="grid gap-3 sm:grid-cols-[120px_1fr]"><div><label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cantidad</label><Input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(index, { quantity: Math.max(1, Number(e.target.value || 1)) })} /></div><div className="rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-600">Subtotal de línea: <span className="font-semibold text-slate-900">{money((selectedProduct.priceCents ?? 0) * Math.max(1, Number(item.quantity || 1)), selectedProduct.currency)}</span></div></div>
                            </div>
                          ) : (
                            <div className="space-y-3"><div className="flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-slate-900">Producto {index + 1}</p><p className="text-xs text-slate-500">Selecciona una línea para completar la orden.</p></div><Button type="button" variant="outline" onClick={() => removeItem(index)} disabled={form.items.length === 1}><Trash2 className="mr-2 h-4 w-4" />Eliminar</Button></div><select className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm" value={item.productId} onChange={(e) => updateItem(index, { productId: e.target.value })}><option value="">Selecciona un producto</option>{productOptions.map((product) => <option key={product.id} value={product.id}>{product.name} · {product.sku} · {money(product.priceCents, product.currency)}</option>)}</select></div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-4 rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100"><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium text-slate-600">Subtotal</span><span className="text-base font-semibold text-slate-900">{money(totals.subtotal)}</span></div><div className="mt-2 flex items-center justify-between gap-3"><span className="text-sm font-medium text-slate-600">Total</span><span className="text-lg font-bold text-slate-900">{money(totals.total)}</span></div></div>
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-2 border-t border-slate-200 pt-2"><Button type="button" variant="outline" onClick={goToPreviousStep}>Atrás</Button><Button type="button" className="bg-[#075f55] text-white hover:bg-[#064d46]" onClick={goToNextStep} disabled={!stepTwoReady}>Siguiente</Button></div>
          </div>
        )}

        {wizardStep === 3 && (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900"><ShoppingCart className="h-4 w-4 text-[#075f55]" /><h3 className="text-base font-semibold">Detalles y confirmación</h3></div>
            <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-sm font-semibold text-slate-900">Tipo de entrega</p><div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">{[{ value: 'delivery', label: 'Delivery', icon: Truck },{ value: 'pickup', label: 'Pickup', icon: Home },{ value: 'cash_on_delivery', label: 'Cobro destino', icon: ShoppingCart },{ value: 'shipping', label: 'Envío nacional', icon: Package },{ value: 'store_pickup', label: 'Retiro en tienda', icon: Home }].map((option) => { const isActive = fulfillmentMode === option.value; const Icon = option.icon; return (<button key={option.value} type="button" onClick={() => setForm((current) => ({ ...current, fulfillmentMethod: option.value }))} className={['rounded-xl border px-3 py-3 text-left transition-colors', isActive ? 'border-[#075f55]/30 bg-[#075f55]/5 text-[#075f55]' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'].join(' ')}><Icon className="h-4 w-4" /><p className="mt-2 text-sm font-semibold">{option.label}</p></button>) })}</div></div>
                {isDeliveryMode && (<div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-sm font-semibold text-slate-900">Dirección de entrega</p><div className="mt-3 space-y-3"><Input value={form.fulfillmentAddress} onChange={(e) => setForm((current) => ({ ...current, fulfillmentAddress: e.target.value }))} placeholder="Dirección completa" /><Input value={form.fulfillmentReference} onChange={(e) => setForm((current) => ({ ...current, fulfillmentReference: e.target.value }))} placeholder="Referencia adicional" /></div></div>)}
                {isPickupMode && (<div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-sm font-semibold text-slate-900">Sucursales disponibles</p><div className="mt-3 grid gap-2 sm:grid-cols-2">{pickupOptions.map((branch) => (<button type="button" key={branch} onClick={() => setForm((current) => ({ ...current, fulfillmentReference: branch }))} className={['rounded-xl border px-3 py-3 text-left text-sm transition-colors', form.fulfillmentReference === branch ? 'border-[#075f55]/30 bg-[#075f55]/5 text-[#075f55]' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'].join(' ')}>{branch}</button>))}</div></div>)}
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="flex items-center justify-between gap-3"><p className="text-sm font-semibold text-slate-900">Observaciones</p><span className="text-xs text-slate-500">{form.notes.length} caracteres</span></div><textarea value={form.notes} onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))} rows={4} maxLength={500} className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:border-[#075f55] focus:ring-1 focus:ring-[#075f55]/20" placeholder="Observaciones de la orden" /><div className="mt-3 flex flex-wrap gap-2">{NOTE_TEMPLATES.map((template) => (<button key={template} type="button" onClick={() => appendNoteTemplate(template)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">{template}</button>))}</div></div>
              </div>
              <div className="space-y-4"><div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><p className="text-sm font-semibold text-slate-900">Resumen final</p><div className="mt-3 space-y-3 text-sm text-slate-600"><div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Cliente</p><p className="mt-1 font-semibold text-slate-900">{form.customerName || selectedContact?.name || 'Sin cliente'}</p><p className="text-xs text-slate-500">{form.customerPhone || selectedContact?.phone || selectedContact?.waContact || 'Sin teléfono'}</p></div><div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Productos</p><p className="mt-1 font-semibold text-slate-900">{selectedProducts.length} línea{selectedProducts.length === 1 ? '' : 's'}</p><p className="text-xs text-slate-500">Subtotal {money(totals.subtotal)}</p></div><div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Canal</p><p className="mt-1 font-semibold text-slate-900">{editChannelInstance ? `${editChannelInstance.instanceName} · ${editChannelInstance.channelType}` : 'Sin canal seleccionado'}</p></div><div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Entrega</p><p className="mt-1 font-semibold text-slate-900">{getFulfillmentModeLabel(fulfillmentMode)}</p><p className="text-xs text-slate-500">{isDeliveryMode ? form.fulfillmentAddress || 'Falta dirección' : form.fulfillmentReference || 'Falta sucursal'}</p></div></div></div><div className="rounded-2xl border border-[#075f55]/15 bg-[#075f55]/5 p-4"><div className="flex items-center gap-2 text-[#075f55]"><CheckCircle2 className="h-4 w-4" /><p className="text-sm font-semibold">Listo para guardar</p></div><p className="mt-2 text-sm text-slate-600">Revisa el resumen y guarda los cambios sin perder el historial de la orden.</p></div></div>
            </div>
            <div className="flex flex-col gap-3 border-t border-slate-200 pt-2 sm:flex-row sm:items-center sm:justify-between"><Button type="button" variant="outline" onClick={goToPreviousStep}>Atrás</Button><Button type="button" className="bg-[#075f55] px-5 py-3 text-white hover:bg-[#064d46]" onClick={onSubmit} disabled={isSaving || !stepThreeReady}>{isSaving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>) : (<><ShoppingCart className="mr-2 h-4 w-4" />Guardar cambios</>)}</Button></div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default EditOrderWizardModal
